'use client';

import { useEffect, useState } from 'react';
import { useCoachAcademyStore } from '@/store/coachAcademy';
import { coachService } from '@/services/coach';
import { PageHeader } from '@/components/layout/PageHeader';
import { CoachAvailabilityEditor } from '@/components/coach/CoachAvailabilityEditor';
import { AlertCircle } from 'lucide-react';

export default function CoachAvailabilityPage() {
  const { academyId, academySlug } = useCoachAcademyStore();
  const [coachId, setCoachId] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!academyId || !academySlug) return;
      try {
        const profiles = await coachService.getMyCoachProfiles();
        if (cancelled) return;
        const activeProfile = profiles.find((profile) => profile.academySlug === academySlug);
        if (!activeProfile) {
          setError('Coach profile not found for this academy');
          setCoachId('');
          return;
        }
        setCoachId(activeProfile.id);
        setError(null);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load coach profile');
        setCoachId('');
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [academyId, academySlug]);

  return (
    <div className="contents">
      <PageHeader
        title="My Availability"
        help={<p>Set your weekly schedule and manage date exceptions</p>}
      />

      {error && (
        <div className="mb-6 p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <p className="text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}

      {coachId && academyId ? (
        <CoachAvailabilityEditor coachId={coachId} academyId={academyId} audience="self" layout="page" />
      ) : null}
    </div>
  );
}
